// The audit log, read (spec/07-identity-auth-and-access.md §11; F-ORG-14 #71, F-SUP-06 #238).
//
// Four endpoints, two audiences:
//
//   GET  /v1/organizations/{o}/audit          an owner's view of their own organisation's log
//   POST /v1/organizations/{o}/audit/export   the same filtered set, as CSV
//   GET  /v1/hq/audit                         HQ's view of every organisation's log
//   POST /v1/hq/audit/export                  the same, as CSV
//
// All four need `audit:read` (spec/07 §6.3), at organisation scope for the first two and at
// platform scope for the HQ pair. The organisation's rows are filtered twice — by the scoped
// repository in code and by row-level security in the database (spec/03 §7.1) — so another
// organisation's owner sees none of them, and a platform-level row (`organizationId: null`) is
// visible to no organisation at all.
//
// **An HQ read is itself audited, with a reason** (spec/07 §7, §11). `reason` is therefore a
// required parameter on both HQ endpoints: every organisation whose rows come back gets an
// `audit.log.read` row in its own log naming who looked and why, and the read writes one
// platform-level row of its own.
//
// **Exports are the filtered set, exactly.** Not the first N of it: an export that matches more
// rows than `AUDIT_EXPORT_MAX_ROWS` is refused with `422` and a detail saying so, because a CSV
// that silently stops is a CSV somebody settles a dispute with. Every cell that a spreadsheet
// would read as a formula is neutralised (spec/20 §11, OWASP "CSV injection").
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { AuditEntryIdSchema, OrganizationIdSchema, RequestIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { AUDIT_READ } from "../permissions/index.js";
/** The OpenAPI extension a route declares its permission in. The server's guard reads it. */
export const PERMISSION_EXTENSION = 'x-clavian-permission';
/**
 * The OpenAPI extension a route declares a **field permission** in: a permission that gates no route
 * of its own, but unmasks part of this route's answer for a caller who holds it — `players:read_contact`
 * on the player routes, whose phone number and email address are masked without it (spec/13 §8,
 * spec/07 §6.3). It counts as gating the route for the rule that every permission gates something
 * (`apps/server/src/modules/__tests__/registry.test.ts`), so it can be neither declared and unused nor
 * used and undeclared. The server reads it from here, like the route's own permission.
 */
export const UNMASKED_BY_EXTENSION = 'x-clavian-unmasked-by';
/**
 * The OpenAPI extension a route declares an **others' permission** in: the permission a caller needs, on top of
 * the route's own, to act on a record another member owns — `shifts:close_any` on closing a shift, whose own
 * permission (`shifts:own`) covers only the caller's own shift (spec/07 §6.3, spec/13 §10; F-OPS-03, F-OPS-05).
 * Like `x-clavian-unmasked-by`, it counts as gating the route for the rule that every permission gates something
 * (`apps/server/src/modules/__tests__/registry.test.ts`); the route's handler checks it against the record. Also
 * `stations:maintenance_in_session` on putting a station into maintenance while another person's session — a player's
 * — runs on it (spec/11 §7, F-LCK-09).
 */
export const OTHERS_PERMISSION_EXTENSION = 'x-clavian-others-permission';
/**
 * The OpenAPI extension a route declares a **limit lift** in: the `…_unlimited` permission that lifts the setting's
 * cap on what the route may grant (spec/07 §6.4) — `sessions:comp_unlimited` on the comp (F-SES-16). Like
 * `x-clavian-unmasked-by`, it counts as gating the route for the rule that every permission gates something
 * (`apps/server/src/modules/__tests__/registry.test.ts`); the route's handler reads it into `effectiveRoleLimit`.
 */
export const UNLIMITED_BY_EXTENSION = 'x-clavian-unlimited-by';
/**
 * The OpenAPI extension a route declares a **narrower permission** in (spec/07 §6.3, 2026-10-07): a permission that
 * also admits a caller to the route, for part of what it does — `inventory:supplies` on the stock writes, which a
 * caller who lacks the route's own permission may make for **supply** items only (ADR-0040 §16). The guard admits a
 * caller holding either; the handler asks whether the caller holds the route's own permission and, when it does not,
 * refuses anything outside the narrower one's part with `403 forbidden`. Like `x-clavian-unmasked-by`, it counts as
 * gating the route for the rule that every permission gates something (`apps/server/src/modules/__tests__/registry.test.ts`).
 */
export const NARROW_PERMISSION_EXTENSION = 'x-clavian-narrow-permission';
/** Who acted (spec/07 §11). `system` is the worker acting on nobody's behalf. */
export const AUDIT_ACTOR_KINDS = ['user', 'member', 'device', 'api_key', 'system'];
/** What an action was taken under, when it was not the actor's own authority (spec/07 §8). */
export const AUDIT_ON_BEHALF_OF_KINDS = ['support_grant', 'impersonation'];
/** The most rows one export will carry. Past it the request is refused, never truncated. */
export const AUDIT_EXPORT_MAX_ROWS = 10_000;
/** The CSV's media type. */
export const CSV_CONTENT_TYPE = 'text/csv';
export const AuditActorSchema = z
    .object({
    kind: z.enum(AUDIT_ACTOR_KINDS),
    /** The user, device or API key id. Null for `system`. */
    id: z.string().nullable(),
    /** A name to show: the member's display name, the job's name. Null when none was recorded. */
    label: z.string().nullable(),
})
    .openapi('AuditActor', { description: 'Who performed an audited action.' });
export const AuditOnBehalfOfSchema = z
    .object({
    kind: z.enum(AUDIT_ON_BEHALF_OF_KINDS),
    /** The break-glass grant, or the impersonation session. What an owner filters on. */
    id: z.string().min(1).max(128),
    /** For an impersonation: the HQ user who was acting as the member. */
    by: z.string().min(1).max(128).optional(),
})
    .openapi('AuditOnBehalfOf', {
    description: 'The break-glass grant or impersonation an action was taken under (spec/07 §8).',
});
export const AuditEntrySchema = z
    .object({
    id: AuditEntryIdSchema,
    /** When the thing happened. Not always when it was recorded: an offline station settles later. */
    occurredAt: IsoDateTimeSchema,
    /** When the row was written. The list is ordered by this, newest first. */
    recordedAt: IsoDateTimeSchema,
    actor: AuditActorSchema,
    actorIp: z.string().nullable(),
    // Absent, rather than null, when the actor acted on their own authority. `.nullable()` on an
    // object component makes the *component* nullable in the generated document — every client
    // would then type `AuditOnBehalfOf` itself as possibly null, and the C# generator refuses the
    // `anyOf` a union would produce. Optional says the same thing without either problem.
    onBehalfOf: AuditOnBehalfOfSchema.optional(),
    /** Null for a platform-level action, which only HQ can see. */
    organizationId: OrganizationIdSchema.nullable(),
    venueId: VenueIdSchema.nullable(),
    /** `sessions.comp`, `org.setting.update` — `<module>.<verb>`, in the glossary's words. */
    action: z.string(),
    targetType: z.string(),
    /** The uuid of the row the action was about, or null when the target is not a row. */
    targetId: z.string().nullable(),
    /** What changed, as a JSON diff, with personal data masked. */
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    reason: z.string().nullable(),
    /** The `X-Request-Id` of the request that did it — which is also its trace id. */
    requestId: RequestIdSchema.nullable(),
})
    .openapi('AuditEntry', { description: 'One row of the audit log (spec/07 §11).' });
export const AuditEntryListSchema = paginated(AuditEntrySchema, 'AuditEntryList');
/**
 * `sessions.comp`, or a prefix written `sessions.*`. Lower snake segments and dots only, so the
 * value can never become a `LIKE` pattern (spec/08 §2.4).
 */
const AUDIT_ACTION_FILTER = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*(?:\.\*)?$/;
/**
 * The filters both views share (spec/13 §15: actor, action, target, date; spec/07 §8: the grant).
 *
 * `from`/`to` are instants, half-open, and apply to `occurredAt`. They are instants rather than
 * the venue-local dates spec/08 §2.4 uses for business reports because an organisation's log spans
 * venues that may keep different time zones: "yesterday" is computed by the console in the venue's
 * zone and sent as the two instants that bound it.
 */
const auditFilterShape = {
    actorId: z
        .string()
        .min(1)
        .max(128)
        .optional()
        .openapi({ description: 'Only rows by this actor (a user, device or API key id).' }),
    actorKind: z.enum(AUDIT_ACTOR_KINDS).optional().openapi({ description: 'Only rows by this kind of actor.' }),
    action: z
        .string()
        .max(120)
        .regex(AUDIT_ACTION_FILTER, 'Expected an action such as sessions.comp, or a prefix such as sessions.*')
        .optional()
        .openapi({ description: 'An action (`sessions.comp`), or every action under a prefix (`sessions.*`).' }),
    targetType: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Expected a target type such as player')
        .optional()
        .openapi({ description: 'Only rows about this kind of thing: `player`, `setting_value`.' }),
    targetId: z
        .string()
        .min(1)
        .max(64)
        .optional()
        .openapi({ description: 'Only rows about this one thing: its uuid or its TypeID. A player’s Audit tab is this filter.' }),
    venueId: VenueIdSchema.optional().openapi({ description: 'Only rows recorded against this venue.' }),
    onBehalfOf: z
        .string()
        .min(1)
        .max(128)
        .optional()
        .openapi({ description: 'Only rows taken under this break-glass grant or impersonation (spec/07 §8).' }),
    requestId: RequestIdSchema.optional().openapi({
        description: 'Only rows written by this request — the `X-Request-Id` a caller quotes to support.',
    }),
    from: IsoDateTimeSchema.optional().openapi({ description: 'Only rows that occurred at or after this instant.' }),
    to: IsoDateTimeSchema.optional().openapi({ description: 'Only rows that occurred before this instant.' }),
};
/** Why an HQ user is reading. Required: every such read is audited with its reason (spec/07 §7). */
const reasonShape = {
    reason: z
        .string()
        .trim()
        .min(3)
        .max(500)
        .openapi({ description: 'Why you are reading. Recorded, with you, in the log of every organisation whose rows you see.' }),
};
export const AuditFilterSchema = z.object(auditFilterShape).strict().openapi('AuditFilter');
export const AuditListQuerySchema = PageQuerySchema.extend(auditFilterShape).strict().openapi('AuditListQuery');
export const AuditExportRequestSchema = z.object(auditFilterShape).strict().openapi('AuditExportRequest');
const platformFilterShape = {
    ...auditFilterShape,
    organizationId: OrganizationIdSchema.optional().openapi({ description: 'Only this organisation’s rows.' }),
    ...reasonShape,
};
export const PlatformAuditListQuerySchema = PageQuerySchema.extend(platformFilterShape)
    .strict()
    .openapi('PlatformAuditListQuery');
export const PlatformAuditExportRequestSchema = z
    .object(platformFilterShape)
    .strict()
    .openapi('PlatformAuditExportRequest');
const csvResponse = (description) => ({
    description,
    headers: {
        'Content-Disposition': { description: 'attachment; filename="…csv"', schema: { type: 'string' } },
    },
    content: { [CSV_CONTENT_TYPE]: { schema: z.string() } },
});
const exportTooLarge = problemResponse(`The filter matches more than ${AUDIT_EXPORT_MAX_ROWS} rows. Narrow it; an export is never truncated.`, 'unprocessable');
export const listOrganizationAuditRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/audit',
    operationId: 'listOrganizationAudit',
    tags: ['audit'],
    summary: 'The organisation’s audit log',
    description: 'Every staff and HQ action in this organisation that changed money, time, access or configuration, ' +
        'or read something sensitive, newest first. Personal data in `before` and `after` is masked. Needs ' +
        '`audit:read` (spec/07 §11).',
    [PERMISSION_EXTENSION]: AUDIT_READ,
    request: { params: OrganizationScopeParamsSchema, query: AuditListQuerySchema },
    responses: {
        200: jsonResponse(AuditEntryListSchema, 'A page of this organisation’s audit rows.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, or it is not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const exportOrganizationAuditRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/audit/export',
    operationId: 'exportOrganizationAudit',
    tags: ['audit'],
    summary: 'Export the organisation’s audit log as CSV',
    description: 'Exactly the rows the filter matches, as CSV, with every cell a spreadsheet would read as a formula ' +
        'neutralised. The export is itself recorded in the log. Needs `audit:read`.',
    [PERMISSION_EXTENSION]: AUDIT_READ,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: AuditExportRequestSchema } }, required: true },
    },
    responses: {
        200: csvResponse('The filtered rows, as CSV.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, or it is not one you can see.', 'not-found'),
        422: exportTooLarge,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listPlatformAuditRoute = createRoute({
    method: 'get',
    path: '/v1/hq/audit',
    operationId: 'listPlatformAudit',
    tags: ['audit', 'hq'],
    summary: 'The platform audit log',
    description: 'Every organisation’s audit rows and the platform’s own, newest first. The read is audited: each ' +
        'organisation whose rows are returned gets a row in its own log naming you and your `reason`. ' +
        'Needs `audit:read` at platform scope.',
    [PERMISSION_EXTENSION]: AUDIT_READ,
    request: { query: PlatformAuditListQuerySchema },
    responses: {
        200: jsonResponse(AuditEntryListSchema, 'A page of audit rows.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const exportPlatformAuditRoute = createRoute({
    method: 'post',
    path: '/v1/hq/audit/export',
    operationId: 'exportPlatformAudit',
    tags: ['audit', 'hq'],
    summary: 'Export the platform audit log as CSV',
    description: 'The HQ view’s filtered set as CSV, audited like the read. Needs `audit:read` at platform scope.',
    [PERMISSION_EXTENSION]: AUDIT_READ,
    request: {
        body: { content: { 'application/json': { schema: PlatformAuditExportRequestSchema } }, required: true },
    },
    responses: {
        200: csvResponse('The filtered rows, as CSV.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        422: exportTooLarge,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** The columns of an audit export, in order. The header row is exactly these. */
export const AUDIT_EXPORT_COLUMNS = [
    'id',
    'occurredAt',
    'recordedAt',
    'actorKind',
    'actorId',
    'actorLabel',
    'actorIp',
    'onBehalfOf',
    'organizationId',
    'venueId',
    'action',
    'targetType',
    'targetId',
    'before',
    'after',
    'reason',
    'requestId',
];
//# sourceMappingURL=audit.js.map